/**
 * @file        worker-data.js
 *              Interact with `worker*` tables in the scheduler database via Jnu to store information
 *              about a running worker.
 *
 * @author      Ryan Saweczko, ryansaweczko@distributive.network
 * @date        Nov 2024
 */
'use strict';

const dcpEnv  = require('dcp/common/dcp-env');
const osBasic = require('dcp/common/os-basic');

const { BigNumber }          = require('dcp/types');
const { requireNative }      = require('dcp/dcp-client/webpack-native-bridge');
const { friends }            = require('../../worker/consts');
const { jnuConnect }         = require('.');

const debugging = require('dcp/debugging').scope('worker');
var taskInfoSerials = {};
const sandboxStats = {};

/**
 * Retrieve the last serial number stored in the db at stored it (or 0 if none) as the
 * taskInfoSerialSym property of the worker instance.
 */
async function recoverTaskInfoSerial(worker)
{
  if (worker.config.unmanaged)
    return;

  try
  {
    const db = await jnuConnect();
    const cursor = await db.select({
      table: 'w$workerTaskInfo',
      prototype: {
        workerOpaqueId: worker.id
      },
      limit: 1,
      order: [{ taskTime: 'desc' }], /* find newest entry */
    });

    if (cursor.rowCount !== 1)
      taskInfoSerials[worker.id] = 0;
    else
      taskInfoSerials[worker.id] = (await cursor.getFirst()).serial;
  }
  catch(error)
  {
    if (error.code !== 'EHTTP_STATUS_502')
      throw error;
    taskInfoSerials[worker.id] = 0;
  }
}

/** Increment the internal serial number and return the new (next) value */
async function incrementTaskInfoSerial(worker)
{
  if (!taskInfoSerials[worker.id])
    await recoverTaskInfoSerial(worker);

  taskInfoSerials[worker.id] = (taskInfoSerials[worker.id] + 1) % (worker.config.taskHistoryLength || 15);
  return taskInfoSerials[worker.id];
}

function getExtIface()
{
  const os = requireNative('os');
  const firstExtIface = Object.values(os.networkInterfaces()).filter(a => a[0]?.internal === false)[0];
  if (!firstExtIface)
    return;
  for (let alias of firstExtIface)
    if (alias.family === 'IPv4')
      return alias;
  return firstExtIface[0];
}

/**
 * Update data for workerRunInfo. When called, send a change request if any of the information has been
 * changed.
 *
 * @param {DistributiveWorker} worker - the worker instance we are reporting on
 * @param {object}             extra  - extra info the caller knows about the worker that cannot be
 *                                      derived directly from the instance
 * @param {boolean}            force  - re-write the record even if nothing has changed
 * @returns {boolean} true if database was changed or the worker is unmanaged
 */
exports.updateRunInfo = async function updateRunInfo(worker, extra, force)
{
  var newInfo = Object.assign({}, extra);
  var neu = false;
  const fnStatic = updateRunInfo[worker.id] || { info: {} };
  updateRunInfo[worker.id] = fnStatic;

  if (!dcpEnv.isBrowserPlatform)
  {
    Object.assign(newInfo, {
      hostname: osBasic.hostname(),
      username: osBasic.username(),
      pid:      osBasic.getpid(),
    });
  }

  /** @todo - gpu type, see DCP-4640 */
  switch (dcpEnv.platform)
  {
    case 'nodejs':
    {
      const process = requireNative('process');
      const os      = requireNative('os');

      const extIface = getExtIface();
      Object.assign(newInfo, {
        version: {
          node: process.versions.node,
          v8:   process.versions.v8,
          os:   os.version(),
        },
        ram:            Math.floor(os.totalmem() / 1024 / 1024), /* MB */
        os:             os.platform(),
        arch:           os.arch(),
        cpuType:        os.cpus()[0]?.model,
        localIpAddress: extIface.address,
        macAddress:     extIface.mac,
      });
      break;
    }
    case 'pythonmonkey':
    {
      Object.assign(newInfo, {
        type: 'pythonmonkey',
      });
      break;
    }
    default:
    {
      if (!dcpEnv.isBrowserPlatform)
        throw new Error('unsupported platform');
      if (!newInfo.browser)
        newInfo.browser = {};
      Object.assign(newInfo, {
        ram:  navigator.deviceMemory ? navigator.deviceMemory * 1024 : undefined,
        os:   navigator.platform || navigator.oscpu,
      });

      let possibleUads = navigator.userAgentData?.brands.filter(o => !/Not.A.Brand/.test(o.brand));
      let bestUad = false
          || possibleUads?.find(uad => uad.brand === 'Google Chrome')
          || possibleUads?.find(uad => uad.brand === 'Microsoft Edge')
          || possibleUads?.find(uad => uad.brand !== 'Chromium')
          || possibleUads?.slice(-1)?.[0].brand;

      Object.assign(newInfo.browser, {
        type: false
          || bestUad?.brand
          || navigator.userAgent.match(/^.* ([a-z]+)\/[^ ]*/i)?.[1]
          || 'unknown browser',
        version: false
          || bestUad?.version
          || navigator.userAgent.match(/(^.* [^ ]+\/)([^)]*)[)]?$/)[2]
          || 'unknown version',
      });

      if (newInfo.browser.type === 'Edg')
        newInfo.browser.type = 'Microsoft Edge';
      else if (newInfo.browser.type === 'Version' && navigator.userAgent.indexOf('Nintendo 3DS') !== -1)
        newInfo.browser.type = 'Nintendo 3DS';
      else if (newInfo.browser.type === 'Edge' && navigator.userAgent.indexOf('Xbox') !== -1)
        newInfo.browser.type = 'Xbox';
      else if (navigator.userAgent.startsWith('Dalvik') && newInfo.browser.type !== 'Dalvik')
      {
        let [ type, version ] = navigator.userAgent.split(/[/ ]/g);
        Object.assign(newInfo, { type, version });
      }
      else if (newInfo.browser.type === 'NintendoBrowser')
      {
        if (navigator.userAgent.indexOf('Nintendo WiiU') !== -1)
          newInfo.browser.type = 'Nintendo WiiU';
        else if (navigator.userAgent.indexOf('Nintendo Switch') !== -1)
          newInfo.browser.type = 'Nintendo Switch';
      }

      const url = new URL(window.location.href);
      newInfo.hostname = url.hostname;
      newInfo.username = url.username;
      newInfo.browser.site = url.origin + url.pathname; /* do not include query string or hash */

      if (navigator.userAgentData?.getHighEntropyValues)
      {
        const hev = await navigator.userAgentData.getHighEntropyValues([
          'architecture', 'platformVersion',
        ]);
        newInfo.arch = hev.architecture;
        newInfo.os   = hev.platform;
        if (!newInfo.version)
          newInfo.version = {};
        newInfo.version.os = hev.platformVersion;
      }
    }
  }

  if (newInfo.os?.startsWith('Linux '))
  {
    const matches = newInfo.os.match('(^Linux)(?: )(.*)');
    newInfo.os = matches[0];
    newInfo.arch = newInfo.arch || matches[2];
  }

  Object.assign(newInfo, worker.runInfo, {
    workerOpaqueId:       worker.id,
    earningsAccount:      worker.config.paymentAddress,
    cpuCores:             worker.config.cores.cpu,
    cpuTargetUtilization: worker.config.utilization.cpu,
    gpuTargetUtilization: worker.config.utilization.gpu,
    maxSandboxes:         worker.config.maxSandboxes,
  });
  /* Let dcp-worker application set gpuCores until we have GPU detection integrated into DistributiveWorker /wg feb 2026 */
  if (typeof worker.runInfo.gpuCores === 'undefined')
    newInfo.gpuCores = worker.config.cores.gpu;

  const oldInfo = fnStatic.info;
  for (let prop in newInfo)
  {
    if (newInfo[prop] === oldInfo[prop])
      continue;
    if (newInfo[prop] instanceof BigNumber && newInfo[prop].eq(oldInfo[prop]))
      continue;
    if (typeof newInfo[prop] === 'object' && JSON.stringify(newInfo[prop]) === JSON.stringify(oldInfo[prop]))
      continue;
    fnStatic.info[prop] = newInfo[prop];
    neu = true;
  }

  if (!neu && !force) /* nothing at all has changed - don't bother just reaching out to timestamp */
    return false;

  try
  {
    if (worker.config.unmanaged)
      return true;
    debugging('management') && console.debug(`wm: update run info - status=${fnStatic.info.status}, earnings=${fnStatic.info.earnings}`);
    const db = await jnuConnect();
    await db.changeOrAdd({
      table: 'w$workerRunInfo',
      prototype: fnStatic.info,
    });
  }
  catch(error)
  {
    if (error.code !== 'EHTTP_STATUS_502')
      worker.emit('error', error);
    return false;
  }

  return true;
}

/**
 * Update earnings in db. We maintain a CVO which holds the un-reported (pendings) earnings. This is
 * cleared every time the earnings are reported. The goal is that we want the portal UI to be able to
 * reset the earnings amount to zero any time they want, and the worker just clicks it up and up.
 *
 * updateRunInfo maintains an internal cache of previously-seen elements and potentially reuses them
 * automatically, so we make sure that we always share the same CVO and that it is reset back to a
 * zero-delta as soon as it lands in the db.
 *
 * The in-memory datastructures are updated when the worker in unmanaged, even though the stats won't
 * be written to the database.
 *
 * @param {boolean} force         force a call to updateRunInfo even if there is no earning and the
 *                                update isn't due yet.
 */
exports.updateEarnings = async function updateEarnings(worker, amount, force)
{
  debugging('management') && console.debug(`wm: earn ${amount} credits`);
  const freqMs = 1e3 * (worker.config.updateEarningsMinFrequency || 60);
  const fnStatic = updateEarnings[worker.id] || {};

  if (!fnStatic.nextDate) /* first time we see this worker */
  {
    updateEarnings[worker.id] = fnStatic;
    fnStatic.nextDate = Date.now() + freqMs / 2;
    fnStatic.pendingCvo = { add: new BigNumber(0) }; /* pending is a CVO */
  }
  fnStatic.pendingCvo.add = BigNumber.sum(fnStatic.pendingCvo.add, amount);
  if (!force && Date.now() < fnStatic.nextDate)
    return;
  if (!force && fnStatic.pendingCvo.add.eq(0))
    return;

  /* double-set nextDate so that a slow db increases our interval, and reentrance bugs don't thrash */
  fnStatic.nextDate = Date.now() + (freqMs * 0.75 + Math.random() * freqMs * 0.5);
  try
  {
    const p$db = exports.updateRunInfo(worker, { earnings: fnStatic.pendingCvo });
    fnStatic.pendingCvo = { add: new BigNumber(0) }; /* replace object to get updateRunInfo cache miss */
    await p$db;
  }
  catch(error)
  {
    /* update failed, put this amount into the CVO for next time.
     * !! add.add => increment BigNumber which is the add prop of the cvo.
     */
    fnStatic.pendingCvo.add = fnStatic.pendingCvo.add.plus(amount);
    throw error;
  }
  finally
  {
    fnStatic.nextDate = Math.max(fnStatic.nextDate, Date.now() + (freqMs * 0.75 + Math.random() * freqMs * 0.5));
  }
}

/**
 * As the supervisor learns about slices in the task, we update the internal cache in anticipation that
 * it will be flushed.  The cache is flushed here if the entire task has finished computing. It is also
 * flush as new tasks are received.
 *
 * @param {object} eventDetails         jobId, sliceNumber, status, optional metrics
 *
 * Treat as infallible (is event handler)
 */
exports.updateTaskInfoCache = function updateTaskInfoCache(worker, eventDetails)
{
  const { jobId, metrics, sliceNumber, status } = eventDetails;
  const stiCache = worker.__stiCache;
  if (!stiCache)
    return;

  const task = stiCache.tasks.filter(x=>x).find(t => t?.jobs[jobId]?.slicesLeft.includes(sliceNumber)); /* find correct tasks[serial] */
  if (!task) /* long slice ended after task serial rollover? */
  {
    debugging('management') && console.debug(`wm: slice ${sliceNumber} of job ${jobId} not found int stiCache`);
    return;
  }
  if (task.prototype.currentTask.finished) /* impossible? */
    return;

  function addToAvg(field, value)
  {
    const taskJobMetrics = task.prototype.currentTask.jobs[jobId].metrics;
    taskJobMetrics[field] = ((taskJobMetrics[field] * task.jobs[jobId].slicesDone) + value) / (task.jobs[jobId].slicesDone + 1);
  }

  const sliceIdx = task.jobs[jobId].slicesLeft.indexOf(sliceNumber);
  if (sliceIdx !== -1)
    task.jobs[jobId].slicesLeft.splice(sliceIdx, 1);

  if (status === 'result')
  {
    addToAvg('cpu',        metrics.CPUTime);
    addToAvg('gpu',        metrics.GPUTime);
    addToAvg('in',         metrics.InDataSize);
    addToAvg('out',        metrics.OutDataSize);
    addToAvg('cpuDensity', metrics.CPUDensity);
    addToAvg('gpuDensity', metrics.GPUDensity);
    task.jobs[jobId].slicesDone += 1;
    task.dirty = true;
    debugging('management') && console.debug(`wm: result for slice ${sliceNumber} of job ${jobId}`);
  }
  else if (status === 'return')
    debugging('management') && console.debug(`wm: dropped slice ${sliceNumber} of job ${jobId}`);
  else
    throw new Error('unknown job status for worker task info');

  /* All jobs of task are finished? flush immediately */
  for (let job of Object.values(task.jobs))
  {
    debugging('management') && console.debug(`wm: job ${jobId} has ${job.slicesLeft.length} slices left of job in task serial ${stiCache?.tasks?.indexOf(task)}`);
    if (job.slicesLeft.length !== 0)
      return;
  }

  debugging('management') && console.debug(`wm: finish task serial ${stiCache?.tasks?.indexOf(task)}`);
  task.prototype.currentTask.finished = new Date();
  task.dirty = true;

  exports.flushTaskInfoCache(worker);
}

exports.markActiveTasksFinished = function markActiveTasksFinished(worker, disposition)
{
  const stiCache = worker.__stiCache;
  if (!stiCache)
    return;

  for (let task of stiCache.tasks.filter(t => !t?.finished))
  {
    task.finished = new Date();
    task.disposition = disposition;
    task.dirty = true;
  }
}

/**
 * Flush dirty records in stiCache.tasks to the database
 */
exports.flushTaskInfoCache = async function flushTaskInfoCache(worker)
{
  const stiCache = worker.__stiCache;
  if (!stiCache || worker.config.unmanaged)
    return;

  for (let task of stiCache.tasks.filter(t => t.dirty))
  {
    try
    {
      debugging('management') && console.debug(`flush task info cache, serial=${stiCache.tasks.indexOf(task)}`);
      const db = await jnuConnect();
      const res = await db.changeOrAdd({
        table: 'w$workerTaskInfo',
        prototype: task.prototype,
        original: {
          workerOpaqueId: task.prototype.workerOpaqueId,
          serial:         task.prototype.serial,
        },
      });
      if (res.rowCount === 1)
        task.dirty = false;
    }
    catch (error)
    {
      if (error.code !== 'EHTTP_STATUS_502')
        worker.emit('error', error);
    }
  }
}

/**
 * Add information about the latest task fetch to the database.
 *
 * The internal stiCache builds and (occasionally) flushes prototypes to the database. The data structure
 * looks like this:
 * stiCache
 *   .jobs
 *     .[jobId]
 *       .cgId    // from most recent task message with this job as new job
 *       .metrics // built from supervisor's job cache OR scheduler's new job message
 *       .isGpu
 *   .tasks       // sparse array
 *     [serial]
 *       .sandboxes      // stats snapshot
 *       .prototype      // data for db
 *         .currentTask
 *           .jobs
 *             [jobId]
 *               .sliceCount
 *               .estimation
 *               .metrics
 *               .isGpu
 *               .cgId
 *       .dirty   // false => all data for this task in db
 *       .jobs
 *          [jobId]
 *            .slicesDone   // number
 *            .slicesLeft   // list of slice numbers
 *
 * @param {object} taskResponse - the latest task response
 * @param {object} supervisor - reference to the supervisor object
 */
exports.storeTaskInfo = async function storeTaskInfo(worker, taskResponse)
{
  const supervisor = worker[friends.supervisor];
  const task = taskResponse.payload?.body?.task;
  const stiCache = worker.__stiCache || { tasks: [], jobs: {} }; worker.__stiCache = stiCache;
  const prototype = {
    workerOpaqueId: worker.id,
    taskTime:       new Date(),
    currentTask:    { jobs: {} },
  };

  if (worker.config.unmanaged || !task)
    return;

  const serial = prototype.serial = await incrementTaskInfoSerial(worker);
  stiCache.tasks[serial] = { prototype, dirty: true, jobs: {} };
  prototype.currentTask.sandboxes = sandboxStats[worker.id]?.dbData;

  if (taskResponse.success === true && task)
  {
    /* Collect and cache basic info from new jobs */
    for (let jobId in taskResponse.payload.body.newJobs)
    {
      const job = taskResponse.payload.body.newJobs[jobId];
      stiCache.jobs[jobId] = {
        cgId: job.jobCgId.slice(job.jobId.length + 1),
      }
    }

    /* Collect information about the task so that we can create the workerTaskInfo record and then update
     * it later. Initial metrics are replaced with averaged as the task produces results.
     */
    for (let jobId in task)
    {
      stiCache.tasks[serial].jobs[jobId] = {
        slicesLeft:  task[jobId].map(slice => slice.sliceNumber), /* slices in this task not yet computed */
        slicesDone:  0,                                           /* number of slices computed */
      };

      let metrics = supervisor.jobManagerInventory.find(jm => jm.jobId === jobId)?.metrics;
      if (!metrics?.sliceCPUTime)
        metrics = taskResponse.payload.body.newJobs[jobId].metrics; /* necessary? */
      stiCache.jobs[jobId].metrics = {
        cpu:        metrics.sliceCPUTime,
        gpu:        metrics.sliceGPUTime,
        cpuDensity: metrics.sliceCPUDensity,
        gpuDensity: metrics.sliceGPUDensity,
        in:         metrics.sliceInDataSize,
        out:        metrics.sliceInDataSize,
      };
      stiCache.jobs[jobId].isGpu = Boolean(metrics.sliceGPUDensity);
    }

    /* Discard entries from the job cache when we no longer have tasks referencing the job */
    const taskJobIds = Object.keys(task);
    for (let jobId in stiCache.jobs)
    {
      if (!stiCache.tasks.filter(x=>x).find(task => task?.hasOwnProperty(jobId)) && !taskJobIds.includes(jobId))
        delete stiCache.jobs[jobId];
    }

    for (let jobId of taskJobIds)
    {
      prototype.currentTask.jobs[jobId] = {
        sliceCount: task[jobId].length,
        estimation: Boolean(task[jobId].filter(x=>x).find(x => x.isEstimationSlice)),
        metrics:    Object.assign({}, stiCache.jobs[jobId].metrics), /* will refine as task runs */
        isGpu:      stiCache.jobs[jobId].isGpu,
        cgId:       stiCache.jobs[jobId].cgId, /* scheduler sends "new" job when cgId changes */
      };
    }
  }

  exports.flushTaskInfoCache(worker);
}

/**
 * Event handler for sandbox event; register a sandbox and keep track of its statistics for as long as it exists.
 */
exports.registerSandbox = function registerSandbox(worker, sandbox)
{
  if (!sandboxStats[worker.id])
  {
    const now = Date.now();
    sandboxStats[worker.id] = {
      totalSandboxes:  0,
      busySandboxes:   0,
      lastSnapshotNow: now,
      start: now,
      dbData: {
        averageBusySandboxesShort: 0,
        averageBusySandboxesLong: 0,
        averageIdleSandboxesShort: 0,
        averageIdleSandboxesLong: 0,
      }
    }
  }
  const stats = sandboxStats[worker.id];

  snapshot();
  stats.totalSandboxes += 1;

  sandbox.on('end', () => {
    snapshot();
    stats.totalSandboxes -= 1;
  });

  sandbox.on('slice', () => {
    snapshot();
    stats.busySandboxes += 1;
  });

  sandbox.on('sliceEnd', () => {
    snapshot();
    stats.busySandboxes -= 1;
  });

  /* Calculate averages for the last time period up until now. Average is a sort of CMA, we take the
   * historical average and work the most recent data into it, weighting so that the current duration is
   * worth the correct proportion of the (short or long) duration, and that the historical average is
   * worth the rest.
   *
   * Note: Durations are adjusted so that they are never longer than the total runtime.
   */
  function snapshot()
  {
    const now = Date.now();
    const shortDurationMs = Math.min( 30 * 1e3, (now - stats.start) || 1);
    const longDurationMs  = Math.min(600 * 1e3, (now - stats.start) || 1);
    const thisDurationMs  = (now - stats.lastSnapshotNow);

    const historyShortDurationMs = Math.max(0, shortDurationMs - thisDurationMs);
    const historyLongDurationMs  = Math.max(0, longDurationMs - thisDurationMs);
    const idleSandboxes = stats.totalSandboxes - stats.busySandboxes;

    stats.dbData.averageBusySandboxesShort =
      (stats.dbData.averageBusySandboxesShort * historyShortDurationMs + stats.busySandboxes * thisDurationMs)
      / shortDurationMs;

    stats.dbData.averageBusySandboxesLong =
      (stats.dbData.averageBusySandboxesLong * historyLongDurationMs + stats.busySandboxes * thisDurationMs)
      / longDurationMs;

    stats.dbData.averageIdleSandboxesShort =
      (stats.dbData.averageIdleSandboxesShort * historyShortDurationMs + idleSandboxes * thisDurationMs)
      / shortDurationMs;

    stats.dbData.averageIdleSandboxesLong =
      (stats.dbData.averageIdleSandboxesLong * historyLongDurationMs + idleSandboxes * thisDurationMs)
      / longDurationMs;

    stats.lastSnapshotNow = now;
  }
}
